import { useCallback, useMemo, useReducer, useRef, useState } from 'react';
import { Set } from 'immutable';
import { mapModes, singleSectionModes } from '../constants/map';
import { layerIdFromGeojsonFeatureId } from '../utils/layers';
import { sectionHasGeodata } from '../utils/sections';

const defaultState = {
  activeLayerId: null,
  activeLayerFeatureId: null,
  activeRowIndexes: [],
  activeFieldId: null,
  selectedFieldIds: new Set(),
  selectedLayerFeatureIds: new Set(),
  hasDirtyNote: false,
  confirmNoteModalOpen: false,
  context: {},
};

export const defaultMapZoom = 15;

const getSectionSetFromGroup = (sectionGroup) =>
  sectionGroup ? new Set(sectionGroup.sections.map(({ id }) => id)) : new Set();

const mapStateReducer = (
  state,
  {
    type,
    mapMode,
    activeId, // fieldId, layerId, sectionGroupId, or rowId depending on the mapMode.
    context,
    hasDirtyNote,
    confirmNoteModalOpen,
    newActiveRowIndexes,
    newActiveSectionId,
  },
) => {
  switch (type) {
    case 'setHasDirtyNote': {
      return {
        ...state,
        hasDirtyNote,
      };
    }
    case 'setConfirmNoteModal': {
      return {
        ...state,
        confirmNoteModalOpen,
      };
    }
    case 'setContext': {
      return {
        ...state,
        context,
      };
    }
    case 'reset': {
      return {
        ...defaultState,
        mapMode: mapModes.VIEW,
        context,
      };
    }
    default: {
      // if there's a dirty note, block section change and open confirmation modal
      if (state.hasDirtyNote) {
        return {
          ...state,
          confirmNoteModalOpen: true,
        };
      }
      switch (mapMode) {
        case mapModes.VIEW:
          return {
            ...defaultState,
            mapMode,
            context,
          };
        case mapModes.DRAW_LAYER:
        case mapModes.EDIT_LAYER:
        case mapModes.LAYER_ENGAGED:
          return {
            ...defaultState,
            mapMode,
            context,
            activeLayerId: activeId || state.activeLayerId,
          };
        case mapModes.LAYER_FEATURE_ENGAGED:
        case mapModes.EDIT_LAYER_FEATURE:
        case mapModes.DELETING_LAYER_FEATURE: {
          const activeLayerFeatureId = activeId || state.activeLayerFeatureId;
          return {
            ...defaultState,
            mapMode,
            context,
            activeLayerId: layerIdFromGeojsonFeatureId(activeLayerFeatureId),
            activeLayerFeatureId,
          };
        }
        case mapModes.MULTI_LAYER_FEATURE_SELECT: {
          const { selectActiveLayerFeature } = context || {};
          // If selectActiveLayerFeatureId is true and there was a previously
          // active layer feature, (which means we were in a layer feature mode
          // and not MULTI_LAYER_FEATURE_SELECT), then we want to initialize the
          // selectedLayerFeatureIds set with the previous activeLayerFeatureId.
          const initialFeatureIds =
            selectActiveLayerFeature && state.activeLayerFeatureId
              ? new Set([state.activeLayerFeatureId])
              : state.selectedLayerFeatureIds;
          const initialLayerId = initialFeatureIds.size
            ? layerIdFromGeojsonFeatureId([...initialFeatureIds][0])
            : state.activeLayerId;
          const activeLayerId = layerIdFromGeojsonFeatureId(activeId);
          // Toggle the activeId in the selectedLayerFeatureIds set
          const selectedLayerFeatureIds = initialFeatureIds.has(activeId)
            ? initialFeatureIds.delete(activeId)
            : initialLayerId === activeLayerId
            ? initialFeatureIds.add(activeId)
            : new Set([activeId]);

          return {
            ...defaultState,
            mapMode: selectedLayerFeatureIds.size
              ? mapMode
              : mapModes.LAYER_ENGAGED,
            context,
            activeLayerId,
            selectedLayerFeatureIds,
          };
        }
        case mapModes.ROW_ENGAGED: {
          if (!newActiveRowIndexes) {
            throw new Error('newActiveRowIndexes missing');
          }
          if (!newActiveSectionId) {
            throw new Error('newActiveSectionId missing');
          }

          return {
            ...defaultState,
            mapMode,
            context,
            activeRowIndexes: newActiveRowIndexes,
            activeFieldId: newActiveSectionId,
          };
        }
        case mapModes.SECTION_GROUP_ENGAGED: {
          return {
            ...defaultState,
            mapMode,
            context,
            activeSectionGroupId: activeId || state.activeSectionGroupId,
            selectedFieldIds: getSectionSetFromGroup(context.sectionGroup),
          };
        }
        case mapModes.MULTI_SECTION_SELECT: {
          const { selectActiveField } = context || {};
          // If selectActiveField is true and there was a previously active field,
          // (which means we were in a section mode and not MULTI_SECTION_SELECT),
          // then we want to initialize the selectedFieldIds set with the previous
          // activeFieldId.
          const initialFieldIds =
            selectActiveField && state.activeFieldId
              ? new Set([state.activeFieldId])
              : state.selectedFieldIds;
          const selectedFieldIds = initialFieldIds.has(activeId)
            ? initialFieldIds.delete(activeId)
            : initialFieldIds.add(activeId);
          return {
            ...defaultState,
            mapMode: selectedFieldIds.size ? mapMode : mapModes.VIEW,
            context,
            // Toggle the activeId in the selectedFieldIds set.
            selectedFieldIds,
          };
        }
        default: {
          // Section modes
          return {
            ...defaultState,
            mapMode,
            context,
            activeFieldId: activeId || state.activeFieldId,
          };
        }
      }
    }
  }
};

const useMapState = (sections, sectionGroups, layers, fieldsContext) => {
  // STATE
  const [mapState, dispatchMapState] = useReducer(mapStateReducer, {
    ...defaultState,
    mapMode: mapModes.VIEW,
  });
  const [mapLoaded, setMapLoaded] = useState(false);
  const [zoom, setZoom] = useState(defaultMapZoom);
  const mapRef = useRef();
  const drawRef = useRef(); // NOTE: this is for useDraw, *not* useDrawLayer
  const attributionControlRef = useRef();

  // Manage draw mode state outside of `useDrawLayer` so other components can access/alter it:
  // array of [mode, options]
  const [layerDrawModeConfig, setLayerDrawModeConfig] = useState([]);

  // USAGE: `setLayerDrawMode(mode)` or `setLayerDrawMode(mode, options)`
  const setLayerDrawMode = useCallback((...args) => {
    setLayerDrawModeConfig(args);
  }, []);

  // UPDATES
  const {
    selectedFieldIds,
    activeFieldId,
    activeLayerId,
    activeLayerFeatureId,
    selectedLayerFeatureIds,
    activeSectionGroupId,
    activeRowIndexes,
    mapMode,
    context,
    confirmNoteModalOpen,
    hasDirtyNote,
  } = mapState;

  const activeLayer = useMemo(() => layers[activeLayerId], [
    layers,
    activeLayerId,
  ]);
  const activeLayerFeature = useMemo(
    () =>
      activeLayer?.data?.features?.find(
        ({ properties }) => properties.id === activeLayerFeatureId,
      ),
    [activeLayer, activeLayerFeatureId],
  );
  const activeField = useMemo(() => sections[activeFieldId], [
    sections,
    activeFieldId,
  ]);
  // Use ref so we can access this state in callbacks without it being stale
  const activeFieldRef = useRef(activeField);
  activeFieldRef.current = activeField;

  const selectedLayerFeatures = useMemo(
    () =>
      (activeLayer?.data?.features || []).filter((f) =>
        selectedLayerFeatureIds.has(f.properties?.id),
      ),
    [activeLayer, selectedLayerFeatureIds],
  );

  const setHasDirtyNote = (dirtyNote) => {
    dispatchMapState({ type: 'setHasDirtyNote', hasDirtyNote: dirtyNote });
  };

  const setConfirmNoteModalOpen = (isConfirmNoteModalOpen) => {
    dispatchMapState({
      type: 'setConfirmNoteModal',
      confirmNoteModalOpen: isConfirmNoteModalOpen,
    });
  };

  const setContext = (updatedContext) => {
    dispatchMapState({
      type: 'setContext',
      context: updatedContext,
    });
  };

  const resetMapState = () => {
    dispatchMapState({ type: 'reset' });
    setZoom(defaultMapZoom);
  };

  const activeSectionGroup = sectionGroups[activeSectionGroupId];

  /**
   * @param {string} mode New map mode.
   * @param {string} activeId The fieldId, layerId, sectionGroupId, or rowId
   * depending on the mapMode. Will use previous activeId if not specified and
   * the new mode uses the same id type.
   * @param {Record<string, any>} optionalContext Optional context object for the map mode.
   * @param {Record<string, any>} args Additional args to pass to `dispatchMapState`.
   */
  const setMapMode = (mode, activeId, optionalContext, args = {}) => {
    // If we about to leave a section and the previously
    // active field has no geodata, remove the section.
    const leavingSectionModes = !singleSectionModes.includes(mode);
    const hasNewActiveId = activeId != null && activeId !== activeFieldId;
    const leavingActiveSection =
      activeFieldRef.current && (leavingSectionModes || hasNewActiveId);
    const activeSectionHasGeodata = sectionHasGeodata(activeFieldRef.current);

    dispatchMapState({
      mapMode: mode,
      activeId,
      context: optionalContext,
      sectionGroups,
      ...args,
    });

    // NOTE: this needs to be called after dispatchMapState, but activeFieldRef
    // is funky after the dispatch, so we capture the values before.
    if (leavingActiveSection && !activeSectionHasGeodata) {
      fieldsContext.removeFieldSection(activeFieldId);
    }
  };

  const selectRows = ({ sectionId, rowIndexes }) => {
    if (rowIndexes.length > 0) {
      dispatchMapState({
        mapMode: mapModes.ROW_ENGAGED,
        newActiveRowIndexes: rowIndexes,
        newActiveSectionId: sectionId,
      });
    } else {
      setMapMode(mapModes.SECTION_ENGAGED, sectionId);
    }
  };

  return {
    // Active Field
    selectedFieldIds,
    activeField,
    isActiveField: ({ id }) => id === activeFieldId || selectedFieldIds.has(id),
    // Active Row(s)
    activeRowIndexes,
    selectRows,
    // Active Section Group
    activeSectionGroupId,
    activeSectionGroup,
    // Active Layer
    activeLayerId,
    activeLayer,
    // Active layer feature
    activeLayerFeatureId,
    activeLayerFeature,
    // Selected layer features
    selectedLayerFeatureIds,
    selectedLayerFeatures,
    // Map Mode
    mapMode,
    setMapMode,
    // Context
    context: context || {},
    // Refs
    mapRef,
    drawRef,
    attributionControlRef, // Ref to current attribution control
    // state for useDrawLayer
    layerDrawModeConfig,
    setLayerDrawMode,
    // Map Loaded
    mapLoaded,
    setMapLoaded,
    // Zoom
    zoom,
    setZoom,
    // Reset
    resetMapState,
    hasDirtyNote,
    setHasDirtyNote,
    confirmNoteModalOpen,
    setConfirmNoteModalOpen,
    setContext,
  };
};

export default useMapState;
